<template>
  <li
    :class="classes"
    :title="description"
    :aria-label="accessibleForecastLabel"
    v-on="$listeners"
  >
    <div class="forecast-item__condition">
      <EConditionIcon
        :code="code"
        class="forecast-item__icon"
        data-test-id="forecast-icon"
      />
    </div>
    <div class="forecast-item__temperatures">
      <span class="forecast-item__temperature" data-test-id="forecast-item-temperature">
        {{ temperature }}°
      </span>
      <span
        v-if="secondaryTemperature !== null"
        class="forecast-item__temperature forecast-item__temperature--secondary"
      >
        {{ secondaryTemperature }}°
      </span>
    </div>
    <time
      :datetime="time.toISOString()"
      class="forecast-item__time"
      data-test-id="forecast-item-time"
    >
      {{ label }}
    </time>
  </li>
</template>

<script>
import { isValidDate } from '~~/common/js/universal/validators';
import classes from '~~/common/vue/mixins/classes';
import EConditionIcon from '~/components/weather/icons/condition-icon';
import a11yMixin from './a11y-mixin';

export default {
  name: 'EForecastItem',
  components: {
    EConditionIcon,
  },
  mixins: [classes, a11yMixin],
  props: {
    description: {
      type: String,
      required: true,
    },
    code: {
      type: String,
      required: true,
    },
    secondaryTemperature: {
      type: Number,
      default: null,
    },
    sunrise: {
      type: Date,
      required: true,
    },
    sunset: {
      type: Date,
      required: true,
    },
    temperature: {
      type: Number,
      required: true,
    },
    time: {
      type: Date,
      required: true,
      validator: isValidDate,
    },
    label: {
      type: String,
      required: true,
    },
    selectable: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    selected: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.forecast-item {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: $space-s $space-1s;
  border-radius: $border-radius-l;
  text-align: center;
  flex-shrink: 0;
  flex-basis: percentage(1/7);

  @include mobile-l {
    flex-basis: percentage(1/7);
  }

  @include tablet {
    flex-basis: percentage(1/7);
  }

  @include desktop {
    flex-basis: percentage(1/5);
  }

  @include desktop-l {
    flex-basis: percentage(1/6);
  }
}

.forecast-item__condition {
  margin: auto;
  margin-bottom: $space-1s;
  width: $space-2l;
  height: $space-2l;
  pointer-events: none;
  color: var(--color-decorative-icon);

  @include tablet {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 0;
    width: $space-4l;
    height: $space-4l;
    padding: $space-1s;
    border-radius: $space-4l;
  }
}

.forecast-item__time {
  color: var(--color-text-secondary);
  font-size: $font-s;

  @include tablet {
    font-size: $font-m;
  }
}

.forecast-item__temperatures {
  @include tablet {
    display: flex;
    justify-content: center;
    font-size: $font-l;
  }
}

.forecast-item--selected {
  cursor: default;

  &:hover,
  &:focus {
    background-color: transparent;
  }

  .forecast-item__time {
    text-decoration: underline;
  }

  .forecast-item__condition {
    background-color: var(--color-background-secondary);
  }

  .forecast-item__temperatures {
    font-weight: $font-weight-1l;
  }
}

.forecast-item--selectable {
  cursor: pointer;
  transition: background-color $timing-2s $easing;

  .forecast-item__time {
    transition: color $timing-2s $easing;
  }

  &:hover,
  &:focus {
    background-color: var(--color-background-secondary);
  }
}

.forecast-item__icon {
  width: 100%;
  height: 100%;
}

.forecast-item__temperature {
  margin-top: $space-1s;
  margin-bottom: $space-2s;
}

.forecast-item__temperature--secondary {
  margin-left: $space-1s;
  color: var(--color-text-secondary);
}
</style>
